import React from 'react';
import { X } from 'lucide-react';
import { Modal } from '../UI';
import styles from './FormModal.module.css';

interface FormModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  icon?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg';
  bodyClassName?: string;
  children: React.ReactNode;

  // Footer — optional. If footerActions provided, renders the gray footer strip.
  footerHint?: React.ReactNode;
  footerActions?: React.ReactNode;

  // Convenience — if provided, FormModal renders a Cancel + gradient submit button.
  onSubmit?: () => void;
  submitLabel?: string;
  submitLoadingLabel?: string;
  submitLoading?: boolean;
  submitDisabled?: boolean;
  cancelLabel?: string;
}

const FormModal: React.FC<FormModalProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  icon,
  size = 'lg',
  bodyClassName,
  children,
  footerHint,
  footerActions,
  onSubmit,
  submitLabel,
  submitLoadingLabel,
  submitLoading,
  submitDisabled,
  cancelLabel = 'Cancel',
}) => {
  const showBuiltInFooter = !footerActions && !!onSubmit;
  const showFooter = !!footerActions || showBuiltInFooter;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={size}>
      <div className={styles.hero}>
        <div className={styles.heroLeft}>
          {icon && <div className={styles.iconBox}>{icon}</div>}
          <div>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.sub}>{subtitle}</div>}
          </div>
        </div>
        <button type="button" className={styles.closeBtn} onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>

      <div className={`${styles.body} ${bodyClassName || ''}`}>
        {children}
      </div>

      {showFooter && (
        <div className={styles.footer}>
          <div className={styles.footerHint}>{footerHint}</div>
          <div className={styles.footerActions}>
            {footerActions ?? (
              <>
                <button type="button" className={styles.btnCancel} onClick={onClose}>
                  {cancelLabel}
                </button>
                <button
                  type="button"
                  className={styles.btnPublish}
                  onClick={onSubmit}
                  disabled={submitLoading || submitDisabled}
                >
                  {submitLoading ? (submitLoadingLabel || 'Saving…') : (submitLabel || 'Save')}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
};

export default FormModal;
